import _ from "lodash";
import React, { HTMLAttributes, PropsWithChildren } from "react";
import { Control, Controller, DeepMap, FieldError, RegisterOptions } from "react-hook-form";
import {
  Checkbox,
  CheckboxProps,
  Dropdown,
  DropdownProps,
  Form,
  Icon,
  Input,
  Label,
  Radio,
  Select,
  TextArea,
} from "semantic-ui-react";
import { DropDownMultiTypeSelector, DropDownSelector } from "../features/permits/components/DropDownFilter";
import { humanizeString } from "../lib/humanize-string";
import { ControlledSelect } from "./ControlledSelect";
import Tooltip from "./Tooltip";

import classNames from "classnames";
import { useId } from "react-aria";
import { HTMLInputAutocomplete } from "../types/inputs/InputAutocomplete";
import "./ControlledForm.scss";

export { ControlledSelect };

export const Readonly = ({
  as,
  value,
  id,
  ariaLabel,
  className,
}: {
  as: React.ElementType;
  value: any;
  id?: string;
  ariaLabel?: string;
  className?: string;
}) => {
  const isEmpty = [undefined, null, ""].includes(value);
  return React.createElement(
    as,
    { id, "aria-label": ariaLabel, "className": classNames(className, { "tw-text-grey": isEmpty }) },
    [isEmpty ? "N/A" : value],
  );
};
const isErrorSelector = (errors: any) => (fieldName: string) => !!_.get(errors, `${fieldName}.message`);
const errorMessageSelector = (errors: any) => (fieldName: string) =>
  isErrorSelector(fieldName)
    ? _.get(errors, `${fieldName}.message`)?.replace(fieldName, humanizeString(fieldName))
    : null;

export interface IStrictControlledTextAreaProps {
  control: Control;
  name: string;
  label?: string;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  className?: string;
  errors?: any;
  rows: number;
  style?: any;
  subLabel?: string;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  autoFocus?: boolean;
  defaultValue?: string | any;
  onBlur?: any;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  transform?: { input: (value: any) => string | null | undefined; output: (value: string | null | undefined) => any };
}
interface IControlledTextAreaProps {
  control: any;
  name: string;
  label?: string;
  subLabel?: string;
  placeholder?: string;
  readonly?: boolean; // displays the text area as text that cannot be edited
  disableEdit?: boolean; // displays editing the text area (differs from readonly because it displays the text area as a text area)
  required?: boolean;
  className?: string;
  errors?: any;
  rows?: number;
  style?: any;
  rules?: any;
  autoFocus?: boolean;
  defaultValue?: string | any;
  onFocus?: () => void;
  onBlur?: any;
  onChange?: any;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  transform?: { input: (value: any) => string | null | undefined; output: (value: string | null | undefined) => any };
  id?: string;
  ariaDescribedBy?: string;
}

type IControlledTextArea = <T extends IControlledTextAreaProps = IControlledTextAreaProps>(
  props: T,
) => React.JSX.Element;

export const ControlledTextArea: IControlledTextArea = (props) => {
  const { transform = { input: (value) => value, output: (value) => value }, ariaDescribedBy } = props;
  const isError = isErrorSelector(props.errors)(props.name);
  const id = props.id ?? useId();

  // Looks like ev is an event, not sure of what type though...
  const onBlur = (ev: unknown) => {
    if (props.onBlur) props.onBlur(ev);
  };

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={Object.assign({}, { required: props.required && "This field is required" }, props.rules)}
      render={({ onChange, value, name }) => {
        return (
          <>
            {props.label && <label htmlFor={!props.readonly ? id : undefined}>{props.label}</label>}
            {props.subLabel && <div className='tw-mb-2 tw-mt-[-4px] tw-text-black-light'>{props.subLabel}</div>}
            {props.readonly && (
              <div className='text-area-read-only'>
                <Readonly as='pre' value={transform.input(value || props.defaultValue)} />
              </div>
            )}
            {!props.readonly && (
              <TextArea
                id={id}
                aria-describedby={[ariaDescribedBy, `${props.name}-error`].filter((id) => id).join(" ")}
                aria-invalid={isError}
                style={props.style}
                autoFocus={props.autoFocus}
                placeholder={props.placeholder}
                rows={props.rows}
                name={name}
                value={transform.input(value) ?? ""}
                onChange={(e, { value }) => {
                  onChange(transform.output(value as string));
                  if (props.onChange) props.onChange();
                }}
                onBlur={onBlur}
                onKeyPress={props.onKeyPress}
                required={props.required}
                onFocus={() => {
                  props.onFocus?.();
                }}
                className={classNames("accessible-input", {
                  [props.className!]: !!props.className,
                  "tw-border-[#e0b4b4] tw-bg-[#fff6f6]": isError,
                })}
                readOnly={props.disableEdit}
              />
            )}
            {isError && (
              <Label basic prompt pointing='above' aria-live='polite' id={`${props.name}-error`}>
                {errorMessageSelector(props.errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    />
  );
};

export interface IStrictControlledInputProps<Name extends string, T> {
  name: Name;
  className?: string;
  control: Control<Record<Name, T>>;
  errors?: DeepMap<Record<Name, T>, FieldError>;
  label?: string;
  subLabel?: string;
  inputLabel?: any;
  inputLabelPosition?: "left" | "right" | "left corner" | "right corner";
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  defaultValue?: T;
  type?: HTMLInputElement["type"];
  action?: any;
  style?: any;
  disabled?: boolean;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  icon?: any;
  iconPosition?: any;
  onBlur?: any;
  mask?: Function; // mask to apply to the input value
  fluid?: boolean;
  autoFocus?: boolean;
  tooltip?: JSX.Element;
  unit?: string;
  autoComplete?: HTMLInputAutocomplete;
  underText?: string;
  transform?: { input: (value: any) => string | null; output: (value: string | null) => any };
  inputRef?: any;
}

export interface IControlledInputProps extends HTMLAttributes<any> {
  className?: string;
  control: any;
  name: string;
  label?: string;
  extraLabel?: JSX.Element; // Additional JSX element to be display inline with the label (can be used to add buttons, tooltips, etc.)
  subLabel?: string;
  inputLabel?: any;
  inputLabelPosition?: "left" | "right" | "left corner" | "right corner";
  inputClassName?: string;
  placeholder?: string;
  errors?: any;
  readonly?: boolean;
  required?: boolean;
  defaultValue?: any;
  type?: HTMLInputElement["type"];
  action?: any;
  style?: any;
  disabled?: boolean;
  rules?: Parameters<typeof Controller>[0]["rules"];
  icon?: any;
  iconPosition?: any;
  onBlur?: any;
  onChange?: any;
  mask?: Function; // mask to apply to the input value
  fluid?: boolean;
  autoFocus?: boolean;
  tooltip?: JSX.Element;
  unit?: string;
  autoComplete?: HTMLInputAutocomplete;
  underText?: string;
  transform?: { input: (value: any) => string | null; output: (value: string | null) => any };
  inputRef?: any;
  id?: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
}

type IControlledInput = <T extends IControlledInputProps = IControlledInputProps>(
  props: PropsWithChildren<T>,
) => React.JSX.Element;

export const ControlledInput: IControlledInput = (props) => {
  const isError = isErrorSelector(props.errors)(props.name);
  const id = props.id ?? useId();
  const ariaDescribedBy = props["aria-describedby"];

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      className='controlled-input'
      rules={Object.assign({}, { required: props.required && "This field is required" }, props.rules)}
      render={({ onChange, value, name, ref }) => {
        const defaultValue = props.transform?.input ? props.transform.input(props.defaultValue) : props.defaultValue;
        return (
          <>
            {props.label && (
              <label
                className='tw-inline-block'
                htmlFor={!props.readonly ? id : undefined}
                style={{ marginBottom: !!props.subLabel ? "0" : undefined }}
              >
                {props.label}
                {props.name === "min_days_notice" && (
                  <Tooltip>
                    If set, applicants cannot select dates within the minimum days notice from the date they are
                    applying.
                  </Tooltip>
                )}
                {!!props.tooltip && <Tooltip>{props.tooltip}</Tooltip>}
              </label>
            )}
            {props.extraLabel}
            {props.subLabel && (
              <div className='tw-mb-[4px] tw-mt-[-2px] tw-text-sm tw-text-text-secondary'>{props.subLabel}</div>
            )}

            {props.readonly ? (
              <Readonly
                id={id}
                as='span'
                value={props.transform?.input ? props.transform?.input(value || defaultValue) : value || defaultValue}
              />
            ) : (
              <Input
                className={props.className}
                id={id}
                input={{
                  className: classNames({
                    "tw-bg-[#fff6f6] tw-border-[#e0b4b4]": isError,
                    [props?.inputClassName as string]: !!props.inputClassName,
                  }),
                }}
                aria-describedby={`${ariaDescribedBy} ${props.name}-error`}
                aria-invalid={isError}
                type={props.type}
                icon={props.icon}
                disabled={props.disabled}
                iconPosition={props.iconPosition === "left" || (!props.iconPosition && props.icon) ? "left" : undefined}
                placeholder={props.placeholder}
                fluid={props.fluid}
                error={isError}
                autoFocus={props.autoFocus}
                name={name}
                value={
                  props.transform?.input
                    ? (props.transform.input(value) ?? defaultValue ?? "")
                    : (value ?? defaultValue ?? "")
                }
                onBlur={props.onBlur}
                onChange={(e, { value }) => {
                  // apply mask if one is provided
                  if (props.mask) value = props.mask(value);
                  // apply transformation if provided
                  if (props.transform?.output) (value as string) = props.transform.output(value);
                  onChange(value);
                  if (props.onChange) props.onChange();
                }}
                style={props.style}
                action={props.action}
                label={props.inputLabel}
                labelPosition={props.inputLabelPosition}
                autoComplete={props.autoComplete}
                required={props.required}
                ref={(node) => {
                  if (props.inputRef) props.inputRef.current = node;
                  ref.current = node;
                }}
                min={props.min}
                max={props.max}
                step={props.step}
              >
                {props.children}
              </Input>
            )}
            {props.underText && <div className='input-under-text'>{props.underText}</div>}
            {isError && (
              <Label basic prompt pointing='above' role='alert' aria-live='polite' id={`${props.name}-error`}>
                {errorMessageSelector(props.errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    />
  );
};

interface IControlledSelect {
  control: any;
  name: string;
  label?: string;
  placeholder?: string;
  errors?: any;
  readonly?: boolean;
  search?: boolean;
  selectProps?: any;
  defaultValue?: any;
  disabled?: boolean;
  required?: boolean;
  inline?: boolean;
}

export const ControlledLabelSelect = (props: IControlledSelect) => {
  const step_types: any[] = ["review", "payment", "document", "inspection"];

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      rules={{ required: props.required && "This field is required" }}
      render={({ onChange, value, name }) => {
        return (
          <div className='ControlledLabelSelect'>
            {props.label && (
              <label className='step_type_label' htmlFor={name}>
                {props.label}
              </label>
            )}
            <div className='selector'>
              {!props.readonly &&
                step_types.map((type: string) => (
                  <Label
                    circular
                    key={type}
                    className={`step_type_selector ${value === type ? "active" : ""}`}
                    onClick={() => onChange(type)}
                    tabIndex={0}
                    onKeyPress={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter") {
                        onChange(type);
                      }
                    }}
                  >
                    {_.capitalize(type)}
                  </Label>
                ))}
            </div>
            {!value && (
              <Label basic prompt pointing='above'>
                This field is required
              </Label>
            )}
          </div>
        );
      }}
    />
  );
};

interface IControlledTagInput {
  control: any;
  name: string;
  label?: string;
  placeholder?: string;
  noResultsMessage?: string;
  errors?: any;
  rules?: any;
  selectProps?: any;
  required?: boolean;
  defaultValue?: any;
  singleSelect?: boolean;
  type?: string;
  title?: any;
}

export const ControlledTagInput = (props: IControlledTagInput) => {
  const isError = isErrorSelector(props.errors)(props.name);
  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        const options = value?.map((item: any) => ({ key: item, text: item, value: item }));
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <Select
              search
              selection
              multiple
              allowAdditions
              options={options}
              name={name}
              error={isError}
              value={value || []}
              placeholder={props.placeholder ?? ""}
              required={props.required}
              noResultsMessage={props.noResultsMessage}
              onChange={(e: any, data: any) => onChange(data.value)}
              {...props.selectProps}
            />
          </>
        );
      }}
    ></Controller>
  );
};

interface IControlledCheckbox extends Omit<HTMLAttributes<any>, "onClick"> {
  name: string;
  className?: string;
  control?: any;
  label?: any;
  subLabel?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  radio?: boolean;
  toggle?: boolean;
  defaultValue?: any;
  onBlur?: any;
  type?: any;
  style?: any;
  customAriaLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLInputElement, MouseEvent>, data: CheckboxProps) => void;
  transform?: {
    input: (value: any) => any;
    output: (value: any) => any;
  };
  labelOptions?: [string, string];
  id?: string;
}

export const ControlledCheckbox = ({
  name,
  control,
  label,
  subLabel,
  disabled,
  required,
  readonly,
  radio,
  toggle,
  defaultValue,
  onBlur,
  type,
  style,
  className,
  onClick,
  customAriaLabel,
  transform = {
    input: (value) => value,
    output: (value) => value,
  },
  labelOptions,
  id: inputId,
  ...rest
}: IControlledCheckbox) => {
  const id = inputId ?? useId();

  const generateLabel = (value: any) => {
    if (labelOptions) {
      return (
        <label htmlFor={!readonly ? id : undefined}>
          {labelOptions[transform.input(value) == "true" || transform.input(value) === true ? 1 : 0]}
        </label>
      );
    }
    if (label) {
      return <label htmlFor={!readonly ? id : undefined}>{label}</label>;
    }
  };
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required && "This field is required" }}
      defaultValue={defaultValue}
      render={({ onChange, value, name }) => {
        return readonly ? (
          <div className='tw-flex'>
            <Icon
              size='large'
              name={
                transform.input(value) == "true" || transform.input(value) === true
                  ? "check square outline"
                  : "square outline"
              }
            />
            <div>
              {generateLabel(value)}
              {subLabel && <div className='tw-text-black-light'>{subLabel}</div>}
            </div>
          </div>
        ) : (
          <div>
            <span className='tw-flex'>
              <Checkbox
                id={id}
                name={name}
                type={type}
                aria-label={customAriaLabel || label}
                aria-describedby={rest["aria-describedby"]}
                label={generateLabel(value)}
                checked={transform.input(value) == "true" || transform.input(value) === true}
                radio={radio}
                onClick={onClick}
                toggle={toggle}
                disabled={disabled || readonly}
                onChange={(_e, { checked }) => {
                  onChange(transform.output(checked));
                  if (onBlur) {
                    onBlur();
                  }
                }}
                style={style}
                className={className}
                required={required}
              />
              {required && <span className='tw-ml-1 tw-text-red'>*</span>}
            </span>
            {subLabel && <div className='-tw-mt-1 tw-ml-[63px] tw-text-sm tw-text-text-hint'>{subLabel}</div>}
          </div>
        );
      }}
    />
  );
};

/**
 * TODO: Delete entirely, should only be used by AddFeeModal
 * @deprecated
 */
export const ControlledDropdown = (props: IControlledTagInput) => {
  const { name, control, rules, defaultValue, label, singleSelect, placeholder, selectProps, type = "single" } = props;
  const isError = isErrorSelector(props.errors)(props.name);
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ onChange, value, name }) => {
        return (
          <>
            {label && (
              <label htmlFor={name} style={{ fontWeight: "bold" }}>
                {label}
              </label>
            )}
            {type === "single" && (
              <DropDownSelector
                fluid
                multiple
                search
                selection
                singleSelect={singleSelect}
                name={name}
                error={isError}
                closeOnChange={true}
                value={value}
                title='search'
                defaultTitle={placeholder}
                onChange={(data: any) => {
                  onChange(data);
                }}
                {...selectProps}
              />
            )}
            {type === "multi" && (
              <DropDownMultiTypeSelector
                fluid
                multiple
                search
                selection
                singleSelect={singleSelect}
                name={name}
                error={isError}
                closeOnChange={true}
                value={value}
                title='search'
                defaultTitle={placeholder}
                onChange={(data: any) => {
                  onChange(data);
                }}
                {...selectProps}
              />
            )}
          </>
        );
      }}
    ></Controller>
  );
};

interface IControlledCustomDropdown {
  control?: any;
  name: string;
  label?: string;
  placeholder?: string;
  errors?: any;
  rules?: any;
  clearable?: any;
  allowAdditions?: any;
  options: {
    key?: string;
    value?: any;
    text?: string;
  }[];
  required?: boolean;
  renderLabel?: any;
  style?: any;
  icon?: string;
  multiple?: any;
  defaultValue?: any;
  readonly?: boolean;
  autoFocus?: boolean;
  loading?: boolean;
  onChange?: (value: any) => void;
  onSearchChange?: (value: any) => void;
  onAddItem?: (event: any, data: any) => void;
  customDropdownItems: React.ReactNode[];
}

export const ControlledCustomDropdown = (props: IControlledCustomDropdown) => {
  const isError = isErrorSelector(props.errors)(props.name);
  return (
    <Controller
      {...props}
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        if (props.readonly)
          return (
            <>
              {props.label && <label htmlFor={name}>{props.label}</label>}
              <Readonly as='span' value={value || props.defaultValue} />
            </>
          );
        return (
          <>
            {props.label && <label htmlFor={name}>{props.label}</label>}
            <Dropdown
              placeholder={props.placeholder}
              search
              fluid
              selection
              loading={props.loading}
              searchInput={{ autoFocus: props.autoFocus }}
              renderLabel={props.renderLabel}
              multiple={props.multiple}
              clearable={props.clearable}
              allowAdditions={props.allowAdditions}
              icon={props.icon}
              name={name}
              error={isError}
              value={value}
              options={props.options}
              onChange={(e: any, data: any) => onChange(data)}
              onSearchChange={(e: any, data: any) => {
                if (props.onSearchChange) props.onSearchChange(data.searchQuery);
              }}
              onAddItem={(e, data) => {
                !!props.onAddItem && props.onAddItem(e, data);
              }}
              style={props.style}
            >
              {<Dropdown.Menu>{props.customDropdownItems}</Dropdown.Menu>}
            </Dropdown>
            {isError && (
              <Label basic prompt pointing='above'>
                {errorMessageSelector(props.errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    ></Controller>
  );
};

export interface IStrictControlledDropdownV2Props {
  control?: Control;
  name: string;
  label?: string;
  subLabel?: string;
  placeholder?: string;
  errors?: any;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  clearable?: any;
  allowAdditions?: any;
  // options: StrictDropdownItemProps[]; // TODO: Breaks stuff, needs to be revisited
  options: any[];
  required?: boolean;
  renderLabel?: any;
  style?: any;
  icon?: string;
  multiple?: boolean;
  defaultValue?: any;
  readonly?: boolean;
  loading?: boolean;
  tooltip?: any;
  autoFocus?: boolean;
  additionLabel?: string;
  disabled?: boolean;
  onChange?: (value: any) => void;
  onAddItem?: (event: any, data: any) => void;
  onBlur?: (event: React.FocusEvent<HTMLElement>, data: DropdownProps) => void;
  underText?: string;
}

interface IControlledDropdownV2Props {
  className?: string;
  control?: any;
  name: string;
  label?: string;
  subLabel?: string;
  placeholder?: string;
  errors?: any;
  showErrorHighlight?: boolean;
  rules?: any;
  clearable?: any;
  allowAdditions?: any;
  // options: StrictDropdownItemProps[]; // TODO: Breaks stuff, needs to be revisited
  options: any[];
  required?: boolean;
  renderLabel?: any;
  style?: any;
  icon?: string;
  multiple?: boolean;
  defaultValue?: any;
  readonly?: boolean;
  tooltip?: any;
  loading?: boolean;
  autoFocus?: boolean;
  additionLabel?: string;
  disabled?: boolean;
  onChange?: (value: any) => void;
  onAddItem?: (event: any, data: any) => void;
  onBlur?: (event: React.FocusEvent<HTMLElement>, data: DropdownProps) => void;
  underText?: string;
  selectOnBlur?: boolean;
  onSearchChange?: (value: any) => void;
}

type IControlledDropdownV2 = <T extends IControlledDropdownV2Props = IControlledDropdownV2Props>(
  props: T,
) => React.JSX.Element;

/**
 * @deprecated Use AccessibleDropdown instead
 */
export const ControlledDropdownV2: IControlledDropdownV2 = (props) => {
  const isError = isErrorSelector(props.errors)(props.name);

  return (
    <Controller
      {...props}
      name={props.name}
      control={props.control}
      rules={Object.assign({}, { required: props.required && "This field is required" }, props.rules)}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => {
        const shouldDefaultToArray = value === undefined && props?.multiple;
        value = shouldDefaultToArray ? [] : value;
        const readonlyValue = props.options?.find(
          (option: any) => option.value === (value || props.defaultValue),
        )?.text;
        if (props.readonly)
          return (
            <>
              {props.label && <label htmlFor={name}>{props.label}</label>}
              {props.subLabel && (
                <div style={{ color: "rgba(0, 0, 0, 0.7)", marginTop: "-4px", marginBottom: "4px" }}>
                  {props.subLabel}
                </div>
              )}
              {!props.multiple && <Readonly as='span' value={readonlyValue ?? value} />}
              {props.multiple && <Readonly as='span' value={readonlyValue ?? value.join(" • ")} />}
            </>
          );
        return (
          <>
            {props.label && (
              <label htmlFor={name}>
                {props.label}
                {props.tooltip && <Tooltip>{props.tooltip}</Tooltip>}
              </label>
            )}
            {props.subLabel && (
              <div style={{ color: "rgba(0, 0, 0, 0.7)", marginTop: "-4px", marginBottom: "4px" }}>
                {props.subLabel}
              </div>
            )}
            <Dropdown
              selectOnBlur={props.selectOnBlur}
              className={props.className}
              placeholder={props.placeholder}
              search
              fluid
              selection
              loading={props.loading}
              searchInput={{ autoFocus: props.autoFocus }}
              renderLabel={props.renderLabel}
              multiple={props.multiple}
              clearable={props.clearable}
              allowAdditions={props.allowAdditions}
              additionLabel={props.additionLabel}
              icon={props.icon}
              name={name}
              error={isError || props.showErrorHighlight}
              value={value}
              options={props.options}
              disabled={props.disabled}
              onChange={(_e: any, data: any) => {
                if (props.onChange) {
                  onChange(data.value);
                  props.onChange(data.value);
                } else {
                  onChange(data.value);
                }
              }}
              onAddItem={(e, data) => {
                !!props.onAddItem && props.onAddItem(e, data);
              }}
              onBlur={(e, data) => {
                if (props.onBlur) {
                  props.onBlur(e, data);
                }
              }}
              onSearchChange={(e, data) => {
                if (props.onSearchChange) {
                  props.onSearchChange(data.searchQuery);
                }
              }}
              style={props.style}
            />
            {props.underText && <div className='input-under-text'>{props.underText}</div>}
            {isError && (
              <Label basic prompt pointing='above'>
                {errorMessageSelector(props.errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    ></Controller>
  );
};

interface IControlledAccessibleDropdown {
  control?: any;
  name: string;
  label?: string;
  errors?: any;
  rules?: any;
  options: {
    key?: string;
    value?: string;
    text?: string;
  }[];
  required?: boolean;
  defaultValue?: string;
  readonly?: boolean;
  tooltip?: string;
  disabled?: boolean;
  onChange?: (value: any) => void;
  id?: string;
}

/**
 * TODO: Delete entirely, should only be used by a couple Permits components
 * @deprecated Use AccessibleDropdown instead
 */
export const ControlledAccessibleDropdown = (props: IControlledAccessibleDropdown) => {
  const isError = isErrorSelector(props.errors)(props.name);
  const id = props.id ?? useId();
  return (
    <Controller
      {...props}
      name={props.name}
      control={props.control}
      rules={Object.assign({}, { required: props.required && "This field is required" }, props.rules)}
      defaultValue={props.defaultValue}
      render={({ value, name, onChange, onBlur, ref }) => {
        if (props.readonly)
          return (
            <>
              {props.label && <label>{props.label}</label>}
              <Readonly as='span' value={value || props.defaultValue} />
            </>
          );
        return (
          <>
            {props.label && (
              <label htmlFor={!props.readonly ? id : undefined}>
                {props.label}
                {props.tooltip && <Tooltip>{props.tooltip}</Tooltip>}
              </label>
            )}
            <div className='custom-select-wrapper'>
              <select
                id={id}
                aria-describedby={`${props.name}-error`}
                aria-invalid={isError}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                disabled={props.disabled}
                required={props.required}
                className='accessible-input focus:tw-outline-none'
              >
                <option value=''>{"Select..."}</option>
                {props.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              <Icon name='caret down' />
            </div>
            {isError && (
              <Label basic prompt pointing='above' aria-live='polite' id={`${props.name}-error`}>
                {errorMessageSelector(props.errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    ></Controller>
  );
};

interface IControlledRadioGroup {
  control: any;
  name: string;
  options: {
    key: string;
    value?: string | number;
    text?: string;
    onClick?: () => void;
    ariaLabel?: string;
  }[];
  defaultValue?: string | number;
  direction?: "horizontal" | "vertical";
  readonly?: boolean;
  label?: string;
  disabled?: boolean;
}

export const ControlledRadioGroup = (props: IControlledRadioGroup) => {
  const { direction = "vertical" } = props;

  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => (
        <>
          {props.readonly && <Readonly as='span' value={value} />}
          {!props.readonly && (
            <Form
              as={"div"}
              className={classNames("tw-flex", {
                "tw-flex-col tw-gap-1": direction === "vertical",
                "tw-flex-row tw-items-center tw-gap-6": direction === "horizontal",
              })}
            >
              {props.label && (
                <label htmlFor={name} className='tw-font-bold'>
                  {props.label}
                </label>
              )}
              {props.options.map((option) => (
                <Form.Field key={option.key} className='tw-mb-0'>
                  <Radio
                    key={option.key}
                    label={option.text}
                    name={name}
                    value={option.value}
                    checked={value === option.value}
                    onClick={() => option.onClick && option.onClick()}
                    onChange={(e, { value }) => onChange(value)}
                    aria-label={option.ariaLabel}
                    disabled={props.disabled}
                  />
                </Form.Field>
              ))}
            </Form>
          )}
        </>
      )}
    />
  );
};

interface IControlledInlineRadioGroup {
  control: any;
  name: string;
  options: {
    key: string;
    value?: string | number;
    text?: string;
    disabled?: boolean;
  }[];
  defaultValue?: string | number;
  label?: string;
  isReadOnly?: boolean;
}

export const ControlledInlineRadioGroup = (props: IControlledInlineRadioGroup) => {
  return (
    <Controller
      name={props.name}
      control={props.control}
      defaultValue={props.defaultValue}
      render={({ onChange, value, name }) => (
        <Form.Group inline>
          {props.label && <label>{props.label}</label>}
          {props.options.map((option) => (
            <Form.Field key={option.key}>
              <Radio
                key={option.key}
                label={option.text}
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={(e, { value }) => onChange(value)}
                disabled={option.disabled || props.isReadOnly}
              />
            </Form.Field>
          ))}
        </Form.Group>
      )}
    />
  );
};
